import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import WorkHistoryOutlinedIcon from "@mui/icons-material/WorkHistoryOutlined";
import AddCircleOutlineOutlinedIcon from "@mui/icons-material/AddCircleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DynamicSearchBar from "../Users/CreateNewUser/components/DynamicSearchbar";
import JobTitleDetails from "./JobTitleDetails";
import ManagementPage from "../shared/ManagementPage";
import {
  createJobTitle,
  getAllJobTitles,
  getJobTitleById,
  updateJobTitle,
} from "../../core/services/jobTitle.service";

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    color: "#f5f5f5",
    fontSize: 14,
    height: 46,
    borderRadius: 2,
    background: "rgba(255, 255, 255, 0.04)",
    transition: "all 0.3s ease",
    "& fieldset": {
      borderColor: "rgba(255, 255, 255, 0.15)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(245, 245, 245, 0.8)",
      borderWidth: "1.5px",
    },
    "&.Mui-focused": {
      background: "rgba(255, 255, 255, 0.08)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(255, 255, 255, 0.5)",
    fontSize: 13,
    "&.Mui-focused": {
      color: "#f5f5f5",
    },
  },
};

const actionButtonSx = {
  color: "white",
  borderColor: "rgba(255,255,255,0.3)",
  fontSize: 11,
  px: 1.2,
};

const getJobTitleKey = (jobTitle, index) =>
  jobTitle.jobTitleId ?? jobTitle.jobTitleName ?? `job-title-${index}`;

const JOB_TITLE_TABS = [
  { value: 1, icon: <WorkHistoryOutlinedIcon />, label: "View Job Title" },
  {
    value: 0,
    icon: <AddCircleOutlineOutlinedIcon />,
    label: "Create Job Title",
  },
];

export default function JobTitlePage() {
  const [tabIndex, setTabIndex] = useState(1);
  const [jobTitles, setJobTitles] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [jobTitleName, setJobTitleName] = useState("");
  const [activeJobTitleAction, setActiveJobTitleAction] = useState(null);
  const [jobTitleDetails, setJobTitleDetails] = useState(null);
  const [editForm, setEditForm] = useState({ jobTitleName: "" });
  const [loadingJobTitles, setLoadingJobTitles] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updatingJobTitle, setUpdatingJobTitle] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const filteredJobTitles = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return jobTitles;

    return jobTitles.filter((jobTitle) => {
      const name = String(jobTitle.jobTitleName ?? "").toLowerCase();
      const id = String(jobTitle.jobTitleId ?? "").toLowerCase();
      return name.includes(term) || id.includes(term);
    });
  }, [jobTitles, searchTerm]);

  const selectedJobTitleFromList = useMemo(() => {
    if (!activeJobTitleAction?.jobTitleId) return null;

    return (
      jobTitles.find(
        (jobTitle, index) =>
          String(getJobTitleKey(jobTitle, index)) ===
          String(activeJobTitleAction.jobTitleId),
      ) ?? null
    );
  }, [activeJobTitleAction, jobTitles]);

  const jobTitleForDetails = jobTitleDetails ?? selectedJobTitleFromList;

  useEffect(() => {
    if (!activeJobTitleAction?.jobTitleId) {
      setJobTitleDetails(null);
      return undefined;
    }

    const jobTitleId =
      activeJobTitleAction?.jobTitleApiId ??
      selectedJobTitleFromList?.jobTitleId;

    if (!jobTitleId) {
      setLoadingDetails(false);
      setSnackbar({
        open: true,
        message: "Job title id is missing",
        severity: "error",
      });
      return undefined;
    }

    let cancelled = false;
    setLoadingDetails(true);

    getJobTitleById(jobTitleId)
      .then((data) => {
        if (!cancelled) setJobTitleDetails(data);
      })
      .catch((error) => {
        if (!cancelled) {
          setJobTitleDetails(null);
          setSnackbar({
            open: true,
            message: error?.message || "Failed to load job title details",
            severity: "error",
          });
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingDetails(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeJobTitleAction?.jobTitleId,
    activeJobTitleAction?.jobTitleApiId,
    activeJobTitleAction?.mode,
    selectedJobTitleFromList?.jobTitleId,
  ]);

  useEffect(() => {
    if (activeJobTitleAction?.mode !== "edit" || !jobTitleForDetails) return;

    setEditForm({
      jobTitleName: jobTitleForDetails.jobTitleName ?? "",
    });
  }, [activeJobTitleAction?.mode, activeJobTitleAction?.jobTitleId, jobTitleForDetails]);

  const loadJobTitles = useCallback(async () => {
    setLoadingJobTitles(true);

    try {
      const list = await getAllJobTitles();
      setJobTitles(list);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to load job titles",
        severity: "error",
      });
    } finally {
      setLoadingJobTitles(false);
    }
  }, []);

  useEffect(() => {
    if (tabIndex === 1) {
      loadJobTitles();
    }
  }, [loadJobTitles, tabIndex]);

  const handleCreateJobTitle = async (event) => {
    event.preventDefault();

    if (!jobTitleName.trim()) {
      setSnackbar({
        open: true,
        message: "Please enter job title name",
        severity: "warning",
      });
      return;
    }

    setCreating(true);

    try {
      await createJobTitle({ jobTitleName: jobTitleName.trim() });
      setJobTitleName("");
      setTabIndex(1);
      setSnackbar({
        open: true,
        message: "Job title created successfully",
        severity: "success",
      });
      await loadJobTitles();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to create job title",
        severity: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleJobTitleAction = (jobTitle, jobTitleKey, mode) => {
    setActiveJobTitleAction({
      jobTitleId: jobTitleKey,
      jobTitleApiId: jobTitle?.jobTitleId ?? jobTitle?.JobTitleId ?? null,
      mode,
    });
  };

  const handleEditFormChange = (field, value) => {
    setEditForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleUpdateJobTitle = async (event) => {
    event.preventDefault();

    const jobTitleId =
      jobTitleForDetails?.jobTitleId ??
      activeJobTitleAction?.jobTitleApiId ??
      selectedJobTitleFromList?.jobTitleId;
    const trimmedName = String(editForm.jobTitleName ?? "").trim();

    if (!jobTitleId) {
      setSnackbar({
        open: true,
        message: "Job title id is missing",
        severity: "warning",
      });
      return;
    }

    if (!trimmedName) {
      setSnackbar({
        open: true,
        message: "Please enter job title name",
        severity: "warning",
      });
      return;
    }

    setUpdatingJobTitle(true);

    try {
      await updateJobTitle({
        jobTitleId,
        jobTitleName: trimmedName,
      });
      setSnackbar({
        open: true,
        message: "Job title updated successfully",
        severity: "success",
      });
      await loadJobTitles();
      const refreshed = await getJobTitleById(jobTitleId);
      setJobTitleDetails(refreshed);
      setActiveJobTitleAction({
        jobTitleId: activeJobTitleAction?.jobTitleId,
        jobTitleApiId: jobTitleId,
        mode: "view",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: error?.message || "Failed to update job title",
        severity: "error",
      });
    } finally {
      setUpdatingJobTitle(false);
    }
  };

  return (
    <ManagementPage
      title="Job Title Management"
      icon={<WorkHistoryOutlinedIcon />}
      tabs={JOB_TITLE_TABS}
      tabIndex={tabIndex}
      onTabChange={setTabIndex}
      snackbar={snackbar}
      onSnackbarClose={handleSnackbarClose}
    >
          {tabIndex === 0 && (
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 550,
                mx: "auto",
                mt: 2.5,
                p: 2.5,
                borderRadius: 4,
                display: "flex",
                flexDirection: "column",
                background:
                  "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
                backdropFilter: "blur(12px)",
                border: "1px solid rgba(255, 255, 255, 0.15)",
                boxShadow: "0 10px 30px rgba(0,0,0,0.5)",
                color: "white",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <AddCircleOutlineOutlinedIcon
                  sx={{ mr: 1, fontSize: 22, color: "#facc15" }}
                />
                <Typography
                  fontWeight={600}
                  fontSize={17}
                  letterSpacing={0.5}
                  sx={{ textTransform: "uppercase" }}
                >
                  Create Job Title
                </Typography>
              </Box>

              <Divider sx={{ mb: 3, borderColor: "rgba(255,255,255,0.1)" }} />

              <Stack component="form" spacing={2.5} onSubmit={handleCreateJobTitle}>
                <TextField
                  fullWidth
                  label="Job Title Name"
                  value={jobTitleName}
                  onChange={(event) => setJobTitleName(event.target.value)}
                  placeholder="Enter job title name"
                  sx={fieldSx}
                />

                <Button
                  type="submit"
                  variant="contained"
                  disabled={creating || !jobTitleName.trim()}
                  sx={{
                    width: "180px",
                    mt: 1,
                    alignSelf: "center",
                    py: 1.2,
                    fontSize: 14,
                    fontWeight: 600,
                    textTransform: "none",
                    borderRadius: 2,
                    background: "#4f8a3d",
                    boxShadow: "0 4px 14px 0 rgba(79, 138, 61, 0.4)",
                    "&:hover": {
                      background: "#3f6f31",
                      boxShadow: "0 6px 20px 0 rgba(79, 138, 61, 0.5)",
                    },
                    "&.Mui-disabled": {
                      background: "rgba(255,255,255,0.16)",
                      color: "rgba(255,255,255,0.45)",
                      boxShadow: "none",
                    },
                  }}
                >
                  {creating ? (
                    <CircularProgress size={22} sx={{ color: "white" }} />
                  ) : (
                    "Create Job Title"
                  )}
                </Button>
              </Stack>
            </Paper>
          )}

          {tabIndex === 1 && (
            <Box sx={{ p: 1.5 }}>
              <DynamicSearchBar
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                placeholder="Search job titles..."
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", md: "row" },
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    maxHeight: { xs: "none", md: "56vh" },
                    overflowY: { xs: "visible", md: "auto" },
                    pr: { xs: 0, md: 1 },
                    "&::-webkit-scrollbar": {
                      width: "5px",
                    },
                    "&::-webkit-scrollbar-track": {
                      background: "transparent",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      background: "rgba(255,255,255,0.3)",
                      borderRadius: "10px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      background: "rgba(255,255,255,0.5)",
                    },
                    scrollbarWidth: "thin",
                    scrollbarColor: "rgba(255,255,255,0.3) transparent",
                  }}
                >
                  {loadingJobTitles ? (
                    <Box display="flex" justifyContent="center" minHeight="28vh">
                      <CircularProgress size={26} sx={{ color: "white" }} />
                    </Box>
                  ) : filteredJobTitles.length === 0 ? (
                    <Typography
                      align="center"
                      fontSize={13}
                      sx={{ color: "rgba(255,255,255,0.6)", py: 4 }}
                    >
                      {jobTitles.length === 0
                        ? "No job titles found."
                        : "No job titles match your search."}
                    </Typography>
                  ) : (
                    filteredJobTitles.map((jobTitle, index) => {
                      const jobTitleKey = getJobTitleKey(jobTitle, index);
                      const isSelected =
                        String(activeJobTitleAction?.jobTitleId) ===
                        String(jobTitleKey);
                      const isViewActive =
                        activeJobTitleAction?.mode === "view" && isSelected;
                      const isEditActive =
                        activeJobTitleAction?.mode === "edit" && isSelected;

                      return (
                        <Card
                          key={jobTitleKey}
                          sx={{
                            mb: 1,
                            borderRadius: 2,
                            border: isSelected
                              ? "1px solid rgba(255,255,255,0.35)"
                              : "1px solid rgba(255,255,255,0.15)",
                            backdropFilter: "blur(6px)",
                            background: isSelected
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(255,255,255,0.05)",
                            color: "white",
                          }}
                        >
                          <CardContent sx={{ py: 1, px: 1.5, "&:last-child": { pb: 1 } }}>
                            <Stack direction="row" spacing={1.5} alignItems="center">
                              <Avatar
                                sx={{
                                  bgcolor: "rgba(255,255,255,0.15)",
                                  width: 30,
                                  height: 30,
                                }}
                              >
                                <WorkHistoryOutlinedIcon sx={{ fontSize: 18 }} />
                              </Avatar>

                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography fontWeight={600} fontSize={13}>
                                  {jobTitle.jobTitleName || `Job Title ${index + 1}`}
                                </Typography>
                              </Box>
                            </Stack>
                          </CardContent>

                          <CardActions
                            sx={{
                              justifyContent: "flex-end",
                              py: 0.5,
                              px: 1.5,
                              pt: 0,
                            }}
                          >
                            <Tooltip title="View">
                              <Button
                                size="small"
                                startIcon={
                                  <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={() =>
                                  handleJobTitleAction(jobTitle, jobTitleKey, "view")
                                }
                                sx={actionButtonSx}
                                variant={isViewActive ? "contained" : "outlined"}
                              >
                                View
                              </Button>
                            </Tooltip>

                            <Tooltip title="Edit">
                              <Button
                                size="small"
                                startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                                onClick={() =>
                                  handleJobTitleAction(jobTitle, jobTitleKey, "edit")
                                }
                                sx={actionButtonSx}
                                variant={isEditActive ? "contained" : "outlined"}
                              >
                                Edit
                              </Button>
                            </Tooltip>
                          </CardActions>
                        </Card>
                      );
                    })
                  )}
                </Box>

                <Box sx={{ flex: 1 }}>
                  {jobTitleForDetails || loadingDetails ? (
                    <Paper
                      sx={{
                        borderRadius: 2,
                        background: "rgba(255,255,255,0.05)",
                        backdropFilter: "blur(6px)",
                        border: "1px solid rgba(255,255,255,0.15)",
                      }}
                    >
                      <JobTitleDetails
                        jobTitle={jobTitleForDetails}
                        mode={activeJobTitleAction?.mode}
                        editForm={editForm}
                        fieldSx={fieldSx}
                        updating={updatingJobTitle}
                        loading={loadingDetails}
                        onEditFormChange={handleEditFormChange}
                        onUpdateJobTitle={handleUpdateJobTitle}
                      />
                    </Paper>
                  ) : (
                    <Typography
                      align="center"
                      fontSize={13}
                      sx={{
                        color: "rgba(255,255,255,0.6)",
                        width: "100%",
                        pt: { xs: 2, md: 0 },
                      }}
                    >
                      Select a job title to view details
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          )}
    </ManagementPage>
  );
}
